import {
	AnimatePresence,
	m,
	type TargetAndTransition,
	type Transition,
} from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import type { MD3Size } from "../../../types/md3";
import { Icon } from "../../icon";
import type { ButtonProps } from "../button";
import {
	BUTTON_RADIUS_SPRING,
	BUTTON_SHAPE_MORPH_SPRING,
	BUTTON_SIZE_TOKENS,
} from "../button/button-tokens";
import {
	ButtonGroupDefaults,
	PRESSED_RADIUS_MAP,
	type ShapeSet,
} from "./button-group-defaults";
import type { ButtonGroupProps } from "./types";

// ─── Constants ───────────────────────────────────────────────────────────────

const PILL_BORDER_RADIUS = 9999;

const CHECK_ICON_SIZE = 24;
const CHECK_ICON = (
	<Icon name="check" variant="rounded" size={CHECK_ICON_SIZE} weight={700} />
);

const CHECK_ICON_GAP_MAP: Record<string, number> = {
	xs: 6,
	sm: 8,
	md: 8,
	lg: 12,
	xl: 12,
};

const CHECK_ICON_MOTION: Transition = {
	type: "spring",
	stiffness: 420,
	damping: 34,
	mass: 0.75,
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const AnimatedCheckLabel = React.memo(function AnimatedCheckLabel({
	selected,
	size,
	children,
}: {
	selected: boolean;
	size: string;
	children: React.ReactNode;
}) {
	const gap = CHECK_ICON_GAP_MAP[size] ?? CHECK_ICON_GAP_MAP.sm;

	return (
		<m.span
			layout
			className="inline-flex min-w-0 items-center justify-center whitespace-nowrap align-middle"
		>
			<AnimatePresence initial={false}>
				{selected && (
					<m.span
						key="selected-check"
						aria-hidden="true"
						initial={{ width: 0, opacity: 0, marginRight: 0, x: -4 }}
						animate={{
							width: CHECK_ICON_SIZE,
							opacity: 1,
							marginRight: gap,
							x: 0,
						}}
						exit={{ width: 0, opacity: 0, marginRight: 0, x: -4 }}
						transition={CHECK_ICON_MOTION}
						className="inline-flex shrink-0 items-center justify-center overflow-hidden"
						style={{ willChange: "width, opacity, transform, margin-right" }}
					>
						<span className="inline-flex h-6 w-6 items-center justify-center">
							{CHECK_ICON}
						</span>
					</m.span>
				)}
			</AnimatePresence>
			<m.span layout="position" className="min-w-0 truncate">
				{children}
			</m.span>
		</m.span>
	);
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function resolveConnectedShapes(
	variant: string,
	orientation: string,
	isFirst: boolean,
	isLast: boolean,
	itemSize: MD3Size,
): ShapeSet {
	const defaults = ButtonGroupDefaults.connectedLeadingButtonShapes(itemSize);
	if (variant !== "connected") return defaults;

	if (orientation === "horizontal") {
		if (isFirst && !isLast)
			return ButtonGroupDefaults.connectedLeadingButtonShapes(itemSize);
		if (!isFirst && isLast)
			return ButtonGroupDefaults.connectedTrailingButtonShapes(itemSize);
		if (!isFirst && !isLast)
			return ButtonGroupDefaults.connectedMiddleButtonShapes(itemSize);
	} else {
		if (isFirst && !isLast)
			return ButtonGroupDefaults.connectedTopButtonShapes(itemSize);
		if (!isFirst && isLast)
			return ButtonGroupDefaults.connectedBottomButtonShapes(itemSize);
		if (!isFirst && !isLast)
			return ButtonGroupDefaults.connectedMiddleButtonShapes(itemSize);
	}

	return defaults;
}

interface ResolvedIcon {
	icon: React.ReactNode;
	label: React.ReactNode;
}

function resolveIconAndLabel(
	element: React.ReactElement<ButtonProps>,
	isSelected: boolean,
	itemSize: string,
	showCheck: boolean,
	iconBehavior: ButtonGroupProps["iconBehavior"],
	labelBehavior: ButtonGroupProps["labelBehavior"],
): ResolvedIcon {
	let icon: React.ReactNode = element.props.icon;
	let label: React.ReactNode = element.props.children;

	const animateCheckInLabel =
		showCheck &&
		iconBehavior !== "none" &&
		iconBehavior !== "selected" &&
		label !== undefined;

	if (iconBehavior === "none" || animateCheckInLabel) {
		icon = undefined;
	} else if (iconBehavior === "selected") {
		icon = isSelected ? element.props.icon : undefined;
	}

	if (labelBehavior === "none") {
		label = undefined;
	} else if (labelBehavior === "selected") {
		label = isSelected ? element.props.children : undefined;
	}

	if (animateCheckInLabel && label !== undefined) {
		label = (
			<AnimatedCheckLabel selected={isSelected} size={itemSize}>
				{label}
			</AnimatedCheckLabel>
		);
	}

	return { icon, label };
}

type MotionOverrides = {
	animate: TargetAndTransition;
	whileTap?: TargetAndTransition;
	transition?: Transition;
	layout?: boolean | "position" | "size" | "preserve-aspect";
};

function buildMotionProps(
	variant: string,
	isSelected: boolean,
	isPressed: boolean,
	itemSize: MD3Size,
	shapes: ShapeSet,
	existingAnimate: TargetAndTransition,
	existingWhileTap: TargetAndTransition,
): MotionOverrides {
	const props: MotionOverrides = { animate: { ...existingAnimate } };

	if (variant === "standard") {
		const pressedRadius = PRESSED_RADIUS_MAP[itemSize] ?? PRESSED_RADIUS_MAP.sm;
		const itemSizeTokens =
			BUTTON_SIZE_TOKENS[itemSize] ?? BUTTON_SIZE_TOKENS.sm;
		const isSquish = isSelected || isPressed;

		props.animate.borderRadius = isSquish
			? pressedRadius
			: itemSizeTokens.roundShapeRadius;
		props.whileTap = {
			scale: 0.98,
			...existingWhileTap,
		};
		props.transition = {
			borderRadius: BUTTON_RADIUS_SPRING,
			default: BUTTON_SHAPE_MORPH_SPRING,
		};
		props.layout = true;
	}

	if (variant === "connected") {
		const { tl, tr, br, bl } = isSelected
			? {
					tl: PILL_BORDER_RADIUS,
					tr: PILL_BORDER_RADIUS,
					br: PILL_BORDER_RADIUS,
					bl: PILL_BORDER_RADIUS,
				}
			: shapes.default;

		props.animate = {
			...props.animate,
			borderRadius: undefined,
			borderTopLeftRadius: tl,
			borderTopRightRadius: tr,
			borderBottomRightRadius: br,
			borderBottomLeftRadius: bl,
		};
		props.whileTap = {
			borderRadius: undefined,
			borderTopLeftRadius: shapes.pressed.tl,
			borderTopRightRadius: shapes.pressed.tr,
			borderBottomRightRadius: shapes.pressed.br,
			borderBottomLeftRadius: shapes.pressed.bl,
			...existingWhileTap,
		};
		props.transition = {
			...BUTTON_SHAPE_MORPH_SPRING,
			borderTopLeftRadius: BUTTON_RADIUS_SPRING,
			borderTopRightRadius: BUTTON_RADIUS_SPRING,
			borderBottomRightRadius: BUTTON_RADIUS_SPRING,
			borderBottomLeftRadius: BUTTON_RADIUS_SPRING,
		};
		props.layout = true;
	}

	return props;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const ButtonGroupComponent = React.forwardRef<
	HTMLFieldSetElement,
	ButtonGroupProps
>(
	(
		{
			className,
			variant = "standard",
			orientation = "horizontal",
			fullWidth = false,
			size,
			itemClassName,
			showCheck = false,
			children,
			iconBehavior,
			labelBehavior,
			style,
			...props
		},
		ref,
	) => {
		const [pressedIndex, setPressedIndex] = React.useState<number | null>(null);

		const childrenArray = React.useMemo(
			() => React.Children.toArray(children).filter(React.isValidElement),
			[children],
		);
		const count = childrenArray.length;

		const handlePointerLeaveAndUp = React.useCallback(() => {
			setPressedIndex(null);
		}, []);

		return (
			<fieldset
				ref={ref}
				className={cn(
					"inline-flex p-0 m-0 border-none max-w-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
					orientation === "vertical"
						? "flex-col items-stretch overflow-y-auto overflow-x-hidden"
						: "flex-row overflow-x-auto overflow-y-hidden",
					variant === "standard" ? "gap-3" : "gap-0.5",
					fullWidth && "w-full",
					fullWidth && orientation === "vertical" && "h-full",
					className,
				)}
				style={style}
				onPointerLeave={handlePointerLeaveAndUp}
				onPointerUp={handlePointerLeaveAndUp}
				{...props}
			>
				{childrenArray.map((child, index) => {
					const element = child as React.ReactElement<ButtonProps>;
					const isSelected = element.props.selected === true;
					const isFirst = index === 0;
					const isLast = index === count - 1;
					const itemSize = (size || element.props.size || "sm") as MD3Size;
					const isPressed = pressedIndex === index;

					const { icon: explicitIcon, label: explicitLabel } =
						resolveIconAndLabel(
							element,
							isSelected,
							itemSize,
							showCheck,
							iconBehavior,
							labelBehavior,
						);

					const shapes = resolveConnectedShapes(
						variant,
						orientation,
						isFirst,
						isLast,
						itemSize,
					);

					const existingAnimate =
						typeof element.props.animate === "object" &&
						!Array.isArray(element.props.animate)
							? element.props.animate
							: {};
					const existingWhileTap =
						typeof element.props.whileTap === "object" &&
						!Array.isArray(element.props.whileTap)
							? element.props.whileTap
							: {};

					const motionProps = buildMotionProps(
						variant,
						isSelected,
						isPressed,
						itemSize,
						shapes,
						existingAnimate as TargetAndTransition,
						existingWhileTap as TargetAndTransition,
					);

					const connectedClasses =
						variant === "connected" && isSelected ? "z-20" : "";

					const clonedElement = React.cloneElement(element, {
						key: element.key ?? index,
						tabIndex: isFirst ? 0 : -1,
						size: size || element.props.size,
						icon: explicitIcon,
						children: explicitLabel,
						...(variant === "connected" && {
							colorStyle: element.props.colorStyle || "tonal",
							selectedColorStyle: element.props.selectedColorStyle || "filled",
						}),
						className: cn(
							element.props.className,
							connectedClasses,
							"focus-visible:z-10 hover:z-10 relative min-w-0",
							orientation === "vertical" && "w-full",
							fullWidth && "flex-1",
							itemClassName,
						),
						onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => {
							setPressedIndex(index);
							if (element.props.onPointerDown) element.props.onPointerDown(e);
						},
						...motionProps,
					} as Partial<ButtonProps>);

					return clonedElement;
				})}
			</fieldset>
		);
	},
);

ButtonGroupComponent.displayName = "ButtonGroup";

export const ButtonGroup = React.memo(ButtonGroupComponent);
